import React, { FC, FormHTMLAttributes } from 'react';
import classNames from 'classnames';
import {
  LanguageAcordionList,
  SuccessMessage,
  Error,
  ScreenshotsPreview,
  TimifyLoader,
} from 'components';
import { TextInputWrapper, SelectTagsWrapper, ScreenshotsUploadWrapper } from 'components/forms';
import { Heading, Note, Button } from 'components/ui';
import { Field, FormRenderProps } from 'react-final-form';
import Subscribe from '../AppSettings/components/Subscribe/Subscribe';
import { useQuery } from '@redux-requests/react';
import { AppsConstants } from '../store/constants';
// import { arrayToObject } from 'utils/arrayToObject';
import { t } from 'utils';
import styles from './DistributionForm.module.scss';
import { AppStatus } from '../AppSettings/constants';

export interface IDistributionForm
  extends Omit<FormHTMLAttributes<HTMLFormElement>, 'children'>,
    FormRenderProps {}

const DistributionForm: FC<IDistributionForm> = (props) => {
  const {
    handleSubmit,
    submitting,
    values,
    className,
    submitSucceeded,
    modifiedSinceLastSubmit,
    hasValidationErrors,
    submitError,
    pristine,
  } = props;
  const rootClassName = classNames(styles.root, className);
  const { languages, status } = values;
  const isFormDisabled = status !== AppStatus.DRAFT;
  const { data: countriesData = [] } = useQuery({ type: AppsConstants.APP_COUNTRIES });
  const { data: categoriesData } = useQuery({ type: AppsConstants.APP_CATEGORIES });
  const maxScreenshots = 4;
  const isSubmitSuccess = submitSucceeded && !modifiedSinceLastSubmit;
  const isSubmitFail = submitError && !modifiedSinceLastSubmit;
  const successClasses = classNames(styles['success-message'], 'mt-4', 'mb-0');
  const isSubmitDisabled =
    pristine || submitting || hasValidationErrors || isSubmitFail || isSubmitSuccess;

  return (
    <form className={rootClassName} onSubmit={handleSubmit} noValidate>
      <div className={styles.content}>
        {submitting && <TimifyLoader overlay />}
        <section className={styles.section}>
          <Heading icon='shipping-fast'>{t('distribution.settings')}</Heading>
          <Field
            name='allowedCountries'
            component={SelectTagsWrapper}
            label='app.distribution.settings.country.label'
            placeholder='app.distribution.settings.country.placeholder'
            options={countriesData}
            disabled={isFormDisabled}
            className='mb-3'
          />
          <Field
            name='categories'
            component={SelectTagsWrapper}
            label='app.distribution.settings.categories.label'
            placeholder='app.distribution.settings.categories.placeholder'
            options={categoriesData}
            disabled={isFormDisabled}
            className='mb-3'
          />
          {/* <Field -> Moved to settings tab
            name='plan'
            component={DropdownWrapper}
            label='app.distribution.settings.plan.label'
            placeholder='app.distribution.settings.plan.placeholder'
            options={supportOptions}
            className='mb-3'
            isShowSelected
          /> */}
        </section>
        <section className={styles.section}>
          <Heading icon='gavel'>{t('legal.details')}</Heading>
          <Field
            name='copyright'
            component={TextInputWrapper}
            disabled={isFormDisabled}
            label='app.copyright'
            placeholder='app.copyright.placeholder'
            className='mb-1'
            parse={(value) => value} // needed because if deleted, final-form does not pass the empty srting
          />
          <Field
            name='privacyUrl'
            component={TextInputWrapper}
            disabled={isFormDisabled}
            label='app.privacyPolicy.url'
            placeholder='app.privacyPolicy.url.placeholder'
            className='mb-1'
            parse={(value) => value}
          />
          <Field
            name='tutorialUrl'
            component={TextInputWrapper}
            disabled={isFormDisabled}
            label='app.tutorial.url'
            placeholder='app.tutorial.url.placeholder'
            className='mb-4'
            parse={(value) => value}
          />
        </section>
        <section className={styles.section}>
          <Heading icon='font'>{t('app.marketplace.details')}</Heading>
          <LanguageAcordionList name='languages' disabled={isFormDisabled} items={languages} />
        </section>
        <section className={styles.section}>
          <Heading icon='images'>{t('app.screenshots')}</Heading>
          <Field
            name='gallery'
            component={ScreenshotsUploadWrapper}
            label='apps.screenshots.upload.label'
            placeholder='apps.screenshots.upload.placeholder'
            className='mb-1'
            multiple
            disabled={submitting || isFormDisabled}
            maxFiles={maxScreenshots}
            accept='image/png, image/jpeg'
          />
          <Note className='mb-4'>{t('app.screenshots.upload.label.hintText')}</Note>
          <ScreenshotsPreview name='gallery' screenshots={values.gallery} />
          <SuccessMessage
            className={successClasses}
            isSuccess={submitSucceeded && !modifiedSinceLastSubmit}
          >
            {t('global.changesSaved')}
          </SuccessMessage>
          {submitError && (
            <Error isError={submitError && !modifiedSinceLastSubmit} className='mt-4'>
              {t('errors.somethingWentWrong')}
            </Error>
          )}
          {!isFormDisabled && (
            <Button disabled={isSubmitDisabled} inline className='mt-4'>
              {t('save.changes')}
            </Button>
          )}
        </section>
      </div>
      <div className={styles.aside}>
        <Subscribe status={status} />
      </div>
    </form>
  );
};

export default DistributionForm;
